import { Mail } from "lucide-react";
import LegalLayout, { LegalSection } from "@/components/legal/LegalLayout";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { LANDING } from "@/constants/landingTestIds";

const LAST_UPDATED = "February 1, 2026";

const TermsPage = () => {
  useDocumentMeta(
    "Terms & Conditions | Tracre One",
    "Read the Terms & Conditions for Tracre One, the AI-powered real estate CRM Android app. Understand your rights, responsibilities, and our AI services disclaimer."
  );

  return (
    <LegalLayout
      title="Terms & Conditions"
      lastUpdated={LAST_UPDATED}
      testId={LANDING.termsPage}
    >
      <p className="text-slate-600 leading-relaxed">
        These Terms & Conditions ("Terms") govern your access to and use of the Tracre One
        Android application and related services (collectively, the "Service"), operated by
        Tracre One ("we", "our", "us"). Please read these Terms carefully before using the
        Service.
      </p>

      <LegalSection title="1. Acceptance of Terms">
        <p>
          By downloading, installing, accessing, or using Tracre One, you acknowledge that you
          have read, understood, and agree to be bound by these Terms and our Privacy Policy. If
          you do not agree with any part of these Terms, please do not use the Service.
        </p>
      </LegalSection>

      <LegalSection title="2. Eligibility">
        <p>
          Tracre One is designed for use by licensed and independent real estate agents,
          brokers, property consultants, and agencies who are at least 18 years old and legally
          capable of entering into a binding contract. By using the Service, you represent that
          you meet these requirements.
        </p>
      </LegalSection>

      <LegalSection title="3. User Accounts">
        <p>
          You may be required to create an account to access certain features. You are
          responsible for maintaining the confidentiality of your login credentials and for all
          activity that occurs under your account. Notify us immediately at{" "}
          <a href="mailto:support@tracreoneai.com">support@tracreoneai.com</a> if you suspect any
          unauthorized use of your account.
        </p>
      </LegalSection>

      <LegalSection title="4. Acceptable Use">
        <p>You agree not to use Tracre One to:</p>
        <ul>
          <li>Send unsolicited bulk messages, spam, or use WhatsApp automation outside the real estate/property marketing domain.</li>
          <li>Upload unlawful, misleading, defamatory, or fraudulent property listings or content.</li>
          <li>Attempt to reverse-engineer, decompile, or interfere with the Service's security or infrastructure.</li>
          <li>Violate any applicable local, state, national, or international law or regulation, including messaging and telecom compliance rules.</li>
        </ul>
      </LegalSection>

      <LegalSection title="5. User Responsibilities">
        <p>
          You are solely responsible for the accuracy of the lead, client, and property data you
          enter into the App, for obtaining any necessary consent before contacting leads via
          WhatsApp or other channels, and for complying with applicable data protection and
          telecom marketing laws in your jurisdiction.
        </p>
      </LegalSection>

      <LegalSection title="6. AI Services Disclaimer">
        <p>
          Tracre One's AI-powered features (including WhatsApp automation, lead nurturing
          suggestions, and the Instant Landing Page Builder) generate content and suggestions
          using automated systems. While we strive for accuracy and relevance, AI-generated
          output may occasionally be incomplete, inaccurate, or unsuitable for a specific
          situation. You are responsible for reviewing and approving any AI-generated content
          before it is sent to a lead or published, and Tracre One is not liable for outcomes
          resulting from unreviewed AI-generated communications.
        </p>
      </LegalSection>

      <LegalSection title="7. Subscription & Payments (if applicable)">
        <p>
          Tracre One offers a Free Plan with core CRM and pipeline features at no cost. Premium
          plans, when made available, will unlock advanced AI automation and are subject to the
          pricing, billing cycle, and terms communicated to you at the time of purchase. All fees
          are exclusive of applicable taxes unless stated otherwise, and, unless required by law,
          payments are non-refundable once a billing cycle has commenced.
        </p>
      </LegalSection>

      <LegalSection title="8. Intellectual Property">
        <p>
          The Tracre One name, logo, App design, source code, and all related intellectual
          property are the exclusive property of Tracre One and its licensors. You are granted a
          limited, non-exclusive, non-transferable license to use the App for your internal
          business purposes only. You may not copy, modify, distribute, or create derivative
          works from any part of the Service without our prior written consent.
        </p>
      </LegalSection>

      <LegalSection title="9. Limitation of Liability">
        <p>
          Tracre One is provided on an "as is" and "as available" basis. To the fullest extent
          permitted by law, we shall not be liable for any indirect, incidental, special, or
          consequential damages, including loss of business, revenue, leads, or data, arising
          from your use of or inability to use the Service, third-party API downtime (e.g. AI
          providers, WhatsApp), or user error.
        </p>
      </LegalSection>

      <LegalSection title="10. Indemnification">
        <p>
          You agree to indemnify and hold harmless Tracre One, its officers, employees, and
          partners from any claims, damages, liabilities, and expenses (including reasonable
          legal fees) arising out of your use of the Service, your violation of these Terms, or
          your infringement of any third-party rights.
        </p>
      </LegalSection>

      <LegalSection title="11. Suspension & Termination">
        <p>
          We reserve the right to suspend or terminate your access to the Service, without prior
          notice, if we reasonably believe you have violated these Terms, engaged in abusive or
          spam-like behavior, or used the Service unlawfully. You may also stop using the Service
          and delete your account at any time by contacting our support team.
        </p>
      </LegalSection>

      <LegalSection title="12. Governing Law">
        <p>
          These Terms shall be governed by and construed in accordance with the laws of India,
          without regard to its conflict of law principles. Any disputes arising out of or
          relating to these Terms shall be subject to the exclusive jurisdiction of the courts
          located in India.
        </p>
      </LegalSection>

      <LegalSection title="13. Changes to Terms">
        <p>
          We may revise these Terms from time to time. Material changes will be communicated via
          in-app notice or email, and the "Last updated" date at the top of this page will be
          revised accordingly. Your continued use of the Service after such changes constitutes
          your acceptance of the updated Terms.
        </p>
      </LegalSection>

      <LegalSection title="14. Contact Information">
        <p>For any questions regarding these Terms & Conditions, please contact us:</p>
        <a
          href="mailto:support@tracreoneai.com"
          className="inline-flex items-center gap-2 mt-2 rounded-lg bg-[#2563EB]/10 text-[#2563EB] font-semibold px-4 py-2.5 hover:bg-[#2563EB]/15 transition-colors"
        >
          <Mail className="w-4 h-4" /> support@tracreoneai.com
        </a>
      </LegalSection>
    </LegalLayout>
  );
};

export default TermsPage;
